/**
 * Updates the recently viewed products in localStorage.
 * Stores product objects with display data for instant rendering.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in localStorage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 10;

  /**
   * Adds a product to the recently viewed products list.
   * @param {Object|string} product - Product data object or legacy product ID string.
   * @param {string} product.id - The product ID.
   * @param {string} product.handle - The product handle.
   * @param {string} product.title - The product title.
   * @param {string} product.url - The product URL.
   * @param {string} product.price - The formatted price string.
   * @param {string} product.image - The featured image URL.
   */
  static addProduct(product) {
    let viewedProducts = this.getProducts();

    // Normalize: if a plain string ID is passed (legacy), store minimal object
    const entry =
      typeof product === 'string' ? { id: product } : { ...product };

    if (!entry.id) return;

    // Remove any existing entry for this product
    viewedProducts = viewedProducts.filter(
      (/** @type {Object} */ p) => (p.id || p) !== entry.id
    );

    viewedProducts.unshift(entry);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from localStorage.
   * @returns {Object[]} The list of viewed product objects.
   */
  static getProducts() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      // Normalize legacy format (plain ID strings) to objects
      return raw.map((item) =>
        typeof item === 'string' ? { id: item } : item
      );
    } catch {
      return [];
    }
  }
}
